import { useEffect, useMemo, useState } from 'react';
import type { ReactElement } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link, useSearchParams } from 'react-router';
import { hotelQueryOptions } from '@quoruum/frontend-common/oas/gen/quoruum/@tanstack/react-query.gen';
import type { HotelBean } from '@quoruum/frontend-common/oas/gen/quoruum/types.gen';
import {
  rateScrapeMetadataQueryOptions,
  hotelActionStatusesOptions,
} from '@quoruum/frontend-common/oas/gen/admin/@tanstack/react-query.gen';
import {
  RateScrapeMetadataBean,
  HotelStatusBean,
} from '@quoruum/frontend-common/oas/gen/admin/types.gen';
import { PaginatedTable, type PaginatedTableColumn } from '../components/PaginatedTable';
import {
  SharedScrapeMetadataPanel,
  buildHotelMapHref,
} from '../components/SharedScrapeMetadataPanel';
import {
  BOOKING_ENGINE_TYPE_OPTIONS,
  isBookingEngineType,
  type BookingEngineType,
} from '../constants/bookingEngines';

const BOOKING_ENGINE_TYPE_PARAM = 'bookingEngineType';
const TAB_PARAM = 'tab';
const RSMS_PAGE_SIZE_OPTIONS = [10, 25, 50] as const;
const DEFAULT_RSMS_PAGE_SIZE = 25;
const RSMS_STATUS_KEYS = [
  'mostRecentShopTime',
  'shouldRateShopWithRecency',
  'rateShoppingDisabledReason',
  'learnRateShoppingDisabledReason',
] as const;
type BookingEnginesTab = 'srsm' | 'rsms';

function parseSelectedBookingEngineType(searchParams: URLSearchParams): BookingEngineType | '' {
  const value = searchParams.get(BOOKING_ENGINE_TYPE_PARAM)?.trim() ?? '';
  return isBookingEngineType(value) ? value : '';
}

function parseActiveTab(searchParams: URLSearchParams): BookingEnginesTab {
  const value = searchParams.get(TAB_PARAM)?.trim().toLowerCase();
  return value === 'rsms' ? 'rsms' : 'srsm';
}

function formatHotelAddress(hotel: HotelBean): string {
  const parts = [hotel.address1, hotel.address2, hotel.city?.name].filter(
    (part): part is string => typeof part === 'string' && part.trim().length > 0
  );
  return parts.join(', ') || '-';
}

function formatStatusValue(value: unknown): string {
  if (value === null || value === undefined) {
    return '-';
  }

  if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
    return String(value);
  }

  if (Array.isArray(value)) {
    if (value.length === 0) {
      return '-';
    }

    return value.map((item) => formatStatusValue(item)).join(', ');
  }

  try {
    return JSON.stringify(value);
  } catch {
    return '[unserializable]';
  }
}

function renderSelectedStatusFields(status: Record<string, unknown>): ReactElement | string {
  const entries = RSMS_STATUS_KEYS.flatMap((key) => {
    const value = status[key];
    if (value === undefined || value === null) {
      return [];
    }

    return [[key, formatStatusValue(value)] as const];
  });

  if (entries.length === 0) {
    return '-';
  }

  return (
    <ul className="list-none space-y-0.5">
      {entries.map(([key, value]) => (
        <li key={key} className="text-xs text-slate-700">
          {key}: {value}
        </li>
      ))}
    </ul>
  );
}

export function BookingEngines(): ReactElement {
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState<BookingEnginesTab>(() => parseActiveTab(searchParams));
  const [rsmsPage, setRsmsPage] = useState(1);
  const [rsmsPageSize, setRsmsPageSize] = useState<number>(DEFAULT_RSMS_PAGE_SIZE);
  const [selectedBookingEngineType, setSelectedBookingEngineType] = useState<
    BookingEngineType | ''
  >(() => parseSelectedBookingEngineType(searchParams));

  useEffect(() => {
    const urlTab = parseActiveTab(searchParams);
    const urlValue = parseSelectedBookingEngineType(searchParams);
    setActiveTab((previous) => (previous === urlTab ? previous : urlTab));
    setSelectedBookingEngineType((previous) => (previous === urlValue ? previous : urlValue));
  }, [searchParams]);

  useEffect(() => {
    const nextParams = new URLSearchParams(searchParams);

    if (selectedBookingEngineType) {
      nextParams.set(BOOKING_ENGINE_TYPE_PARAM, selectedBookingEngineType);
    } else {
      nextParams.delete(BOOKING_ENGINE_TYPE_PARAM);
    }

    nextParams.set(TAB_PARAM, activeTab);

    if (nextParams.toString() !== searchParams.toString()) {
      setSearchParams(nextParams, { replace: true });
    }
  }, [activeTab, searchParams, selectedBookingEngineType, setSearchParams]);

  useEffect(() => {
    setRsmsPage(1);
  }, [selectedBookingEngineType]);

  const {
    data: rsmsQueryResponse,
    isLoading: isRsmsLoading,
    isError: hasRsmsError,
    error: rsmsError,
    isFetching: isRsmsFetching,
  } = useQuery({
    ...rateScrapeMetadataQueryOptions({
      query: {
        sourceBookingEngine: selectedBookingEngineType || undefined,
        page: rsmsPage,
        pageSize: rsmsPageSize,
      },
    }),
    enabled: activeTab === 'rsms' && selectedBookingEngineType !== '',
    placeholderData: (previousData) => previousData,
  });

  const rsmsRows = useMemo(() => rsmsQueryResponse?.results ?? [], [rsmsQueryResponse?.results]);

  const hotelIdsForRsmsPage = useMemo(
    () =>
      [...new Set(rsmsRows.map((metadata) => metadata.hotelId))].filter(
        (hotelId): hotelId is string => typeof hotelId === 'string' && hotelId.length > 0
      ),
    [rsmsRows]
  );

  const { data: rsmsHotelsQueryResponse, isFetching: isRsmsHotelsFetching } = useQuery({
    ...hotelQueryOptions({
      query: {
        ids: hotelIdsForRsmsPage,
      },
    }),
    enabled: activeTab === 'rsms' && hotelIdsForRsmsPage.length > 0,
    placeholderData: (previousData) => previousData,
  });

  const {
    data: rsmsHotelsStatusesResponse,
    isLoading: isRsmsStatusesLoading,
    isFetching: isRsmsStatusesFetching,
    isError: hasRsmsStatusesError,
  } = useQuery({
    ...hotelActionStatusesOptions({
      query: {
        id: hotelIdsForRsmsPage,
      },
    }),
    enabled: activeTab === 'rsms' && hotelIdsForRsmsPage.length > 0,
    staleTime: 60 * 1000,
  });

  const hotelNameByHotelId = useMemo(() => {
    const hotelNameMap = new Map<string, string>();

    (rsmsHotelsQueryResponse?.results ?? []).forEach((hotel: HotelBean) => {
      if (typeof hotel.id === 'string' && typeof hotel.name === 'string') {
        hotelNameMap.set(hotel.id, hotel.name);
      }
    });

    return hotelNameMap;
  }, [rsmsHotelsQueryResponse?.results]);

  const hotelAddressByHotelId = useMemo(() => {
    const hotelAddressMap = new Map<string, string>();

    (rsmsHotelsQueryResponse?.results ?? []).forEach((hotel: HotelBean) => {
      if (typeof hotel.id === 'string') {
        hotelAddressMap.set(hotel.id, formatHotelAddress(hotel));
      }
    });

    return hotelAddressMap;
  }, [rsmsHotelsQueryResponse?.results]);

  const hotelStatusByHotelId = useMemo(() => {
    const statusMap = new Map<string, HotelStatusBean>();
    const hotelStatuses = (rsmsHotelsStatusesResponse?.results ?? []) as Array<unknown>;

    for (const status of hotelStatuses) {
      if (!status || typeof status !== 'object') {
        continue;
      }

      const statusId = (status as { id?: unknown }).id;
      if (typeof statusId === 'string' && statusId.length > 0) {
        statusMap.set(statusId, status);
      }
    }

    return statusMap;
  }, [rsmsHotelsStatusesResponse?.results]);

  const rsmsHasNextPage = typeof rsmsQueryResponse?.nextPage === 'number';

  const rsmsColumns = useMemo<ReadonlyArray<PaginatedTableColumn<RateScrapeMetadataBean>>>(
    () => [
      {
        key: 'name',
        header: 'Name',
        renderCell: (metadata) => {
          if (!metadata.hotelId) {
            return '-';
          }

          if (isRsmsHotelsFetching && !hotelNameByHotelId.has(metadata.hotelId)) {
            return 'Loading...';
          }

          return (
            <Link
              to={buildHotelMapHref(metadata.hotelId)}
              className="text-sky-700 underline decoration-sky-300 underline-offset-2 hover:text-sky-800"
            >
              {hotelNameByHotelId.get(metadata.hotelId) ?? metadata.hotelId}
            </Link>
          );
        },
      },
      {
        key: 'status',
        header: 'Status',
        renderCell: (metadata) => {
          if (!metadata.hotelId) {
            return '-';
          }

          const site = metadata.externalId?.site;
          if (!site) {
            return '-';
          }

          const hotelStatus = hotelStatusByHotelId.get(metadata.hotelId);
          if (hasRsmsStatusesError) {
            return 'Error';
          }

          if ((isRsmsStatusesLoading || isRsmsStatusesFetching) && !hotelStatus) {
            return 'Loading...';
          }

          const siteStatus = hotelStatus?.rateScrapeMetadataStatus?.[site];
          if (!siteStatus || typeof siteStatus !== 'object') {
            return '-';
          }

          return renderSelectedStatusFields(siteStatus);
        },
      },
      {
        key: 'address',
        header: 'Address',
        renderCell: (metadata) => {
          if (!metadata.hotelId) {
            return '-';
          }

          if (isRsmsHotelsFetching && !hotelAddressByHotelId.has(metadata.hotelId)) {
            return 'Loading...';
          }

          return hotelAddressByHotelId.get(metadata.hotelId) ?? '-';
        },
      },
      {
        key: 'externalId',
        header: 'externalId',
        renderCell: (metadata) => {
          if (metadata.externalId?.site && metadata.externalId?.id) {
            return `${metadata.externalId.site}:${metadata.externalId.id}`;
          }

          return '-';
        },
      },
    ],
    [
      hasRsmsStatusesError,
      hotelAddressByHotelId,
      hotelNameByHotelId,
      hotelStatusByHotelId,
      isRsmsHotelsFetching,
      isRsmsStatusesFetching,
      isRsmsStatusesLoading,
    ]
  );

  return (
    <section className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm">
      <h1 className="text-2xl font-semibold tracking-tight text-slate-900">Booking Engines</h1>

      <div className="mt-4 flex items-center gap-3">
        <select
          value={selectedBookingEngineType}
          onChange={(event) => {
            setSelectedBookingEngineType(event.target.value as BookingEngineType | '');
          }}
          className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 shadow-sm outline-none transition focus:border-sky-500 focus:ring-2 focus:ring-sky-200"
        >
          <option value="">Select booking engine type</option>
          {BOOKING_ENGINE_TYPE_OPTIONS.map((type) => (
            <option key={type} value={type}>
              {type}
            </option>
          ))}
        </select>
      </div>

      <div
        className="mb-3 mt-3 flex items-center gap-2 border-b border-slate-200 pb-2"
        role="tablist"
        aria-label="Booking engine tabs"
      >
        <button
          type="button"
          role="tab"
          aria-selected={activeTab === 'srsm'}
          aria-controls="booking-engines-tabpanel-srsm"
          id="booking-engines-tab-srsm"
          onClick={() => {
            setActiveTab('srsm');
          }}
          className={`rounded-md px-3 py-1 text-xs font-medium ${
            activeTab === 'srsm' ? 'bg-sky-100 text-sky-800' : 'text-slate-600 hover:bg-slate-100'
          }`}
        >
          SRSM
        </button>

        <button
          type="button"
          role="tab"
          aria-selected={activeTab === 'rsms'}
          aria-controls="booking-engines-tabpanel-rsms"
          id="booking-engines-tab-rsms"
          onClick={() => {
            setActiveTab('rsms');
          }}
          className={`rounded-md px-3 py-1 text-xs font-medium ${
            activeTab === 'rsms' ? 'bg-sky-100 text-sky-800' : 'text-slate-600 hover:bg-slate-100'
          }`}
        >
          RSMs
        </button>
      </div>

      {activeTab === 'srsm' ? (
        <div
          role="tabpanel"
          id="booking-engines-tabpanel-srsm"
          aria-labelledby="booking-engines-tab-srsm"
          className="pt-4"
        >
          {selectedBookingEngineType ? (
            <>
              <div>
                <h2 className="text-lg font-semibold leading-tight text-slate-900">
                  Shared Scrape Metadata ({selectedBookingEngineType})
                </h2>
              </div>

              <div className="mt-4 overflow-hidden rounded-md border border-slate-200">
                <SharedScrapeMetadataPanel
                  bookingEngineType={selectedBookingEngineType}
                  tableKeyPrefix="booking-engines-"
                />
              </div>
            </>
          ) : (
            <p className="mt-4 text-sm text-slate-600">
              Select a booking engine type to view metadata.
            </p>
          )}
        </div>
      ) : (
        <div
          role="tabpanel"
          id="booking-engines-tabpanel-rsms"
          aria-labelledby="booking-engines-tab-rsms"
          className="pt-4"
        >
          {selectedBookingEngineType ? (
            <div className="overflow-hidden rounded-md border border-slate-200">
              <PaginatedTable
                columns={rsmsColumns}
                rows={rsmsRows}
                getRowKey={(metadata, index) => {
                  if (metadata.externalId?.site && metadata.externalId?.id) {
                    return `${metadata.externalId.site}:${metadata.externalId.id}`;
                  }

                  return `${metadata.hotelId ?? 'unknown-hotel'}-${index}`;
                }}
                isLoading={isRsmsLoading}
                isError={hasRsmsError}
                error={rsmsError}
                loadingText="Loading RSMs..."
                errorText="Unable to load RSMs."
                emptyText="No RSMs found for this booking engine."
                page={rsmsQueryResponse?.page ?? rsmsPage}
                hasNextPage={rsmsHasNextPage}
                isFetchingPage={isRsmsFetching}
                onPreviousPage={() => {
                  setRsmsPage((previousPage) => Math.max(1, previousPage - 1));
                }}
                onNextPage={() => {
                  setRsmsPage(rsmsHasNextPage ? (rsmsQueryResponse?.nextPage as number) : rsmsPage);
                }}
                pageSize={rsmsPageSize}
                pageSizeOptions={RSMS_PAGE_SIZE_OPTIONS}
                onPageSizeChange={(nextPageSize) => {
                  setRsmsPageSize(nextPageSize);
                  setRsmsPage(1);
                }}
              />
            </div>
          ) : (
            <p className="text-sm text-slate-600">Select a booking engine type to view RSMs.</p>
          )}
        </div>
      )}
    </section>
  );
}
